import { useStore } from "@/stores/useStore";
import { useMemo, useState } from "react";
import { isToday } from "date-fns";
import { CardFormDialog } from "@/components/Dashboard/components/Card/CardFormDialog";
import { card as cardRepository } from "@/repositories/cards";
import { TagsRepository } from "@/repositories/tags";
import { GoListCardList } from "./GoListCardList";
import { GoListOpenButton } from "./GoListOpenButton";
import { Card } from "@/types/v2";
import { firestoreToDateTime } from "@/lib/utils";

export const GoList = ({
  showOpenButton = false,
}: {
  showOpenButton?: boolean;
}) => {
  const cards = useStore((s) => s.cards);
  const shop = useStore((s) => s.shop);
  const tags = useStore((s) => s.tags);
  const [filter, setFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);

  const todaysCards = useMemo(() => {
    return cards.filter((card) => {
      if (!card.promisedTime) return false;
      return isToday(firestoreToDateTime(card.promisedTime) as Date);
    });
  }, [cards]);

  const completedCards = useMemo(() => {
    const completeContainers = shop?.cardConfig?.goListCompleteContainers ?? [];
    return todaysCards.filter(
      (card) =>
        card.currentContainer &&
        completeContainers.includes(card.currentContainer)
    );
  }, [todaysCards, shop?.cardConfig?.goListCompleteContainers]);

  const inProgressCards = useMemo(() => {
    const completeContainers = shop?.cardConfig?.goListCompleteContainers ?? [];
    return todaysCards.filter(
      (card) =>
        !card.currentContainer ||
        !completeContainers.includes(card.currentContainer)
    );
  }, [todaysCards, shop?.cardConfig?.goListCompleteContainers]);

  // Filtered lists based on RO number
  const filteredCompletedCards = useMemo(() => {
    if (!filter.trim()) return completedCards;
    return completedCards.filter((card) =>
      (card.cardNumber ?? "").toLowerCase().includes(filter.toLowerCase())
    );
  }, [completedCards, filter]);

  const filteredInProgressCards = useMemo(() => {
    if (!filter.trim()) return inProgressCards;
    return inProgressCards.filter((card) =>
      (card.cardNumber ?? "").toLowerCase().includes(filter.toLowerCase())
    );
  }, [inProgressCards, filter]);

  // Calculate total labor hours for header (using unfiltered todaysCards)
  const totalCompletedHours = useMemo(
    () =>
      completedCards.reduce(
        (sum, card) => sum + cardRepository.calculateApprovedHours(card),
        0
      ),
    [completedCards]
  );

  const totalApprovedHours = useMemo(
    () =>
      todaysCards.reduce(
        (sum, card) => sum + cardRepository.calculateApprovedHours(card),
        0
      ),
    [todaysCards]
  );

  // Handle card edit and update
  const handleEditCard = async (values: Partial<Card>) => {
    if (!selectedCard || !shop) return;
    if (values.tags && values.tags?.length > 0) {
      await TagsRepository.addTag(shop.id, values.tags, tags);
    }
    await cardRepository.update(shop.id, selectedCard.id, {
      ...values,
      _isSoftMigrated: selectedCard._isSoftMigrated,
    });
    setDialogOpen(false);
  };

  return (
    <div className="flex h-full w-full flex-col p-4">
      <div className="sticky top-0 z-10 mb-4 flex flex-col gap-2 pb-2">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">
            Go List
            <span className="ml-6 text-lg font-bold text-gray-600">
              {totalCompletedHours} of {totalApprovedHours} Hours Complete
            </span>
          </h2>
          {showOpenButton && <GoListOpenButton />}
        </div>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by RO Number..."
          className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
        />
      </div>
      <div className="min-h-0 flex-1 space-y-6 overflow-auto">
        <GoListCardList
          cards={filteredCompletedCards}
          title="Completed"
          onEdit={(card) => {
            setSelectedCard(card);
            setDialogOpen(true);
          }}
          showSum
        />
        <GoListCardList
          cards={filteredInProgressCards}
          title="In Progress"
          onEdit={(card) => {
            setSelectedCard(card);
            setDialogOpen(true);
          }}
        />
      </div>
      {selectedCard && (
        <CardFormDialog
          title="Edit"
          buttonTitle="Edit Card"
          description="Edit card. Card fields are determined by the card configuration."
          cardData={selectedCard}
          externalTrigger
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSubmit={handleEditCard}
        />
      )}
    </div>
  );
};
